import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import { FP, StatCard } from 'components/financepro';
import EstadoFinancieroShell, {
  fmtMoney,
  fmtMontoContable,
  formatearFecha,
  textoPeriodoCorte,
} from './EstadoFinancieroShell';
import PlantillaEstadoFinanciero, { LAYOUT_ESTADO_SIMPLE } from './PlantillaEstadoFinanciero';
import { buildPlantillaEstadoFinancieroPrint } from './plantillaEstadoFinancieroPrint';

/** Clave del programa para validar permisos (mismo patrón que FacturaPOS). */
export const PROGRAMA_BALANCE_GENERAL = 'balancegeneral';

const COLUMNAS = [
  { id: 'cuenta', label: 'Cuenta', tipo: 'cuenta', ocultarEnMovil: true },
  { id: 'nombre', label: 'Concepto', tipo: 'texto' },
  { id: 'saldo', label: 'Saldo', tipo: 'monto' },
];

const etiquetaSeccion = (titulo) => {
  const t = String(titulo || '').toLowerCase();
  if (t.includes('activo')) return 'Bienes y derechos de la empresa';
  if (t.includes('pasivo')) return 'Obligaciones con terceros';
  if (t.includes('utilidad')) return 'Resultado del período actual y de períodos anteriores';
  if (t.includes('capital') || t.includes('patrimonio')) return 'Patrimonio de los propietarios';
  return '';
};

const esFilaUtilidad = (row) =>
  Boolean(row?.es_utilidad) ||
  Boolean(row?.tipo_utilidad) ||
  String(row?.nombre || '')
    .toLowerCase()
    .includes('utilidad (pérdida)');

const nombreUtilidad = (row, fechaDesde, fechaHasta) => {
  const tipo = String(row?.tipo_utilidad || '').toLowerCase();
  if (tipo === 'anterior' || (!tipo && String(row?.nombre || '').toLowerCase().includes('anterior'))) {
    return fechaDesde
      ? `Utilidad (pérdida) de períodos anteriores (antes del ${formatearFecha(fechaDesde)})`
      : 'Utilidad (pérdida) de períodos anteriores';
  }
  if (tipo === 'actual' || String(row?.nombre || '').toLowerCase().includes('actual')) {
    if (fechaDesde && fechaHasta) {
      return `Utilidad (pérdida) del período actual (${formatearFecha(fechaDesde)} – ${formatearFecha(fechaHasta)})`;
    }
    return 'Utilidad (pérdida) del período actual';
  }
  return row?.nombre || 'Utilidad (pérdida)';
};

const agruparBalance = (filas) => {
  const secciones = [];
  let actual = null;

  (filas || []).forEach((row) => {
    if (row.es_titulo) {
      actual = {
        titulo: row.nombre || row.grupo || '',
        grupo: row.grupo || row.nombre || '',
        cuentas: [],
        total: 0,
      };
      secciones.push(actual);
      return;
    }
    const destino =
      secciones.find(
        (s) =>
          String(s.grupo || '').toLowerCase() === String(row.grupo || '').toLowerCase()
      ) || actual;
    if (destino) {
      destino.cuentas.push(row);
      destino.total = Math.round((destino.total + (parseFloat(row.saldo) || 0)) * 100) / 100;
    }
  });

  const totalDe = (nombre) => {
    const sec = secciones.find((s) =>
      String(s.titulo || s.grupo || '').toLowerCase().includes(nombre)
    );
    return sec ? sec.total : 0;
  };

  const activo = totalDe('activo');
  const pasivo = totalDe('pasivo');
  const capital = totalDe('capital') || totalDe('patrimonio');
  const pasivoMasCapital = Math.round((pasivo + capital) * 100) / 100;
  const diferencia = Math.round((activo - pasivoMasCapital) * 100) / 100;
  const cuadra = Math.abs(diferencia) < 0.005;

  return { secciones, activo, pasivo, capital, pasivoMasCapital, diferencia, cuadra };
};

export const construirModeloBalanceGeneral = ({
  filas,
  fechaDesde,
  fechaHasta,
  nombreEmpresa,
}) => {
  const { secciones, activo, pasivoMasCapital, diferencia, cuadra } = agruparBalance(filas);

  const seccionesModelo = [];
  secciones.forEach((s) => {
    const cuentasUtilidad = s.cuentas.filter(esFilaUtilidad);
    const cuentasResto = s.cuentas.filter((c) => !esFilaUtilidad(c));
    const totalResto =
      Math.round(cuentasResto.reduce((acc, c) => acc + (parseFloat(c.saldo) || 0), 0) * 100) / 100;

    seccionesModelo.push({
      titulo: s.titulo,
      subtitulo: etiquetaSeccion(s.titulo),
      filas: cuentasResto.map((c) => ({
        cuenta: c.cuenta,
        nombre: c.nombre,
        celdas: { saldo: c.saldo },
      })),
      total: { etiqueta: `Total ${s.titulo}`, celdas: { saldo: totalResto } },
    });

    if (cuentasUtilidad.length) {
      const totalUtilidad =
        Math.round(cuentasUtilidad.reduce((acc, c) => acc + (parseFloat(c.saldo) || 0), 0) * 100) /
        100;
      seccionesModelo.push({
        titulo: 'Utilidad',
        subtitulo: fechaDesde
          ? `Período actual y resultado anterior al ${formatearFecha(fechaDesde)}`
          : 'Resultado del período actual y de períodos anteriores',
        filas: cuentasUtilidad.map((c) => ({
          cuenta: c.cuenta,
          nombre: nombreUtilidad(c, fechaDesde, fechaHasta),
          celdas: { saldo: c.saldo },
        })),
        total: { etiqueta: 'Total utilidad', celdas: { saldo: totalUtilidad } },
      });
    }
  });

  return {
    nombreEmpresa,
    tituloDocumento: 'Balance general',
    periodo: textoPeriodoCorte(fechaHasta, fechaDesde),
    columnas: COLUMNAS,
    layout: LAYOUT_ESTADO_SIMPLE,
    mensajeVacio: 'Sin saldos en esta sección',
    secciones: seccionesModelo,
  
    totales: [
      {
        etiqueta: 'Total pasivo y capital',
        variante: 'intermedio',
        celdas: { saldo: pasivoMasCapital },
      },
      {
        etiqueta: cuadra ? 'Balance cuadrado' : 'Descuadre',
        variante: 'neto',
        tono: cuadra ? 'ok' : 'alerta',
        celdas: { saldo: cuadra ? activo : diferencia },
      },
    ],
    nota: 'Activo = Pasivo + Capital · La utilidad suma el período actual y el anterior a la fecha de corte · Los importes negativos se muestran entre paréntesis',
    firmas: [{ etiqueta: 'Preparado por' }, { etiqueta: 'Revisado por' }],
  };
};

const CuerpoBalanceGeneral = (ctx) => {
  const modelo = useMemo(
    () => construirModeloBalanceGeneral(ctx),
    [ctx.filas, ctx.fechaDesde, ctx.fechaHasta, ctx.nombreEmpresa]
  );
  return <PlantillaEstadoFinanciero {...modelo} />;
};

const ResumenBalanceGeneral = ({ filas }) => {
  const { activo, pasivo, capital, cuadra, diferencia } = agruparBalance(filas);

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="Activo"
          value={`$${fmtMoney(activo)}`}
          icon={AccountBalanceWalletOutlinedIcon}
          accent={FP.color.accentBlue}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="Pasivo"
          value={`$${fmtMoney(pasivo)}`}
          icon={CreditCardOutlinedIcon}
          accent={FP.color.warning}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="Capital"
          value={`$${fmtMoney(capital)}`}
          icon={SavingsOutlinedIcon}
          accent={FP.color.navyMuted}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label={cuadra ? 'Balance cuadrado' : 'Descuadre'}
          value={`$${fmtMontoContable(cuadra ? activo : diferencia)}`}
          icon={BalanceOutlinedIcon}
          accent={cuadra ? FP.color.success : FP.color.danger}
        />
      </Grid>
    </Grid>
  );
};

const columns = [
  {
    id: 'nombre',
    label: 'Concepto',
    align: 'left',
    width: '72%',
    render: (_, row) => {
      if (row.es_titulo) return String(row.nombre || row.grupo || '').toUpperCase();
      if (esFilaUtilidad(row)) return nombreUtilidad(row);
      if (row.cuenta) return `${row.cuenta}  ${row.nombre || ''}`;
      return row.nombre || '';
    },
  },
  {
    id: 'saldo',
    label: 'Saldo',
    align: 'right',
    width: '28%',
    render: (v, row) => (row.es_titulo ? '' : `$${fmtMontoContable(v)}`),
  },
];

const BalanceGeneral = () => (
  <EstadoFinancieroShell
    programa={PROGRAMA_BALANCE_GENERAL}
    title="Balance General"
    subtitle="Resumen patrimonial (activo = pasivo + capital)"
    reportTitle="Balance General"
    IconComponent={AccountBalanceOutlinedIcon}
    endpoint="contabilidadbackend_balancegeneral"
    columns={columns}
    emptyMessage="No hay saldos para armar el balance general en el período"
    mapResponseRows={(data) => data.lineas || data.filas || []}
    hideFooter
    footerFromRows={(filas) => {
      const { activo, pasivoMasCapital, cuadra } = agruparBalance(filas);
      if (!filas.length) return '';
      return `Total activo: $${fmtMontoContable(activo)} · Pasivo + capital: $${fmtMontoContable(pasivoMasCapital)}${
        cuadra ? '' : ' · Descuadre'
      }`;
    }}
    renderSummary={(ctx) => <ResumenBalanceGeneral filas={ctx.filas} />}
    renderBody={(ctx) => <CuerpoBalanceGeneral {...ctx} />}
    buildPrintHtml={(ctx) => buildPlantillaEstadoFinancieroPrint(construirModeloBalanceGeneral(ctx))}
  />
);

export default BalanceGeneral;
